import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import TalentImportModal from '../components/TalentImportModal';
import TalentCompare from '../components/TalentCompare';
import AddToListModal from '../components/AddToListModal';

const formatNum = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'K';
  return String(n);
};

const formatPct = (v) => v ? (v * 100).toFixed(1) + '%' : '-';

const tierLabel = (t) => {
  if (t === 1) return { text: 'T1', bg: 'bg-amber-500/20', color: 'text-amber-400' };
  if (t === 2) return { text: 'T2', bg: 'bg-zinc-400/20', color: 'text-zinc-300' };
  if (t === 3) return { text: 'T3', bg: 'bg-orange-800/20', color: 'text-orange-400' };
  return { text: '--', bg: 'bg-zinc-800', color: 'text-zinc-600' };
};

const bssColor = (score) => {
  if (!score) return 'text-zinc-600';
  if (score >= 4) return 'text-green-400';
  if (score >= 3) return 'text-yellow-400';
  return 'text-red-400';
};

const defaultFilters = {
  category: [], tier: [], gender: [], agency: [], category_search: '',
  aud_male_min: '', aud_male_max: '', aud_female_min: '', aud_female_max: '',
  aud_u18_min: '', aud_u18_max: '',
  aud_18_24_min: '', aud_18_24_max: '',
  aud_25_34_min: '', aud_25_34_max: '',
  aud_35_44_min: '', aud_35_44_max: '',
  aud_45_54_min: '', aud_45_54_max: '',
  aud_55_plus_min: '', aud_55_plus_max: '',
  aud_black_min: '', aud_black_max: '', aud_white_min: '', aud_white_max: '',
  aud_asian_min: '', aud_asian_max: '', aud_hispanic_min: '', aud_hispanic_max: '',
  aud_usa_min: '', aud_usa_max: '', aud_canada_min: '', aud_canada_max: '',
  aud_uk_min: '', aud_uk_max: '', aud_australia_min: '', aud_australia_max: '',
  twitch_ccv_min: '', twitch_ccv_max: '',
  twitch_followers_min: '', twitch_followers_max: '',
  youtube_followers_min: '', youtube_followers_max: '',
  instagram_followers_min: '', instagram_followers_max: '',
  tiktok_followers_min: '', tiktok_followers_max: '',
  twitter_followers_min: '', twitter_followers_max: '',
  total_following_min: '', total_following_max: '',
  bss_min: ''
};

const arrayFilterKeys = ['category', 'tier', 'gender', 'agency'];

// Read filters from URL on mount
const parseFiltersFromURL = (sp) => {
  const f = { ...defaultFilters };
  for (const key of Object.keys(f)) {
    const val = sp.get(key);
    if (!val) continue;
    if (arrayFilterKeys.includes(key)) {
      f[key] = val.split(',').filter(Boolean);
    } else {
      f[key] = val;
    }
  }
  return f;
};

const ChipFilter = ({ label, options, filterKey, filters, toggleArrayFilter }) => (
  <div className="mb-3">
    <div className="text-zinc-500 text-xs mb-1">{label}</div>
    <div className="flex flex-wrap gap-1">
      {options.map(o => (
        <button key={o} onClick={() => toggleArrayFilter(filterKey, o)}
          className={`px-2 py-0.5 rounded text-xs ${filters[filterKey]?.includes(o) ? 'bg-nextide-turquoise text-black font-semibold' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
          {o}
        </button>
      ))}
    </div>
  </div>
);

const RangeFilter = ({ label, minKey, maxKey, suffix, filters, setFilters }) => (
  <div className="mb-3">
    <div className="text-zinc-500 text-xs mb-1">{label}</div>
    <div className="flex gap-1 items-center">
      <input type="number" placeholder="Min" value={filters[minKey] || ''} onChange={e => setFilters(prev => ({...prev, [minKey]: e.target.value}))}
        className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-xs" />
      <span className="text-zinc-600 text-xs">-</span>
      <input type="number" placeholder="Max" value={filters[maxKey] || ''} onChange={e => setFilters(prev => ({...prev, [maxKey]: e.target.value}))}
        className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-xs" />
      {suffix && <span className="text-zinc-600 text-xs">{suffix}</span>}
    </div>
  </div>
);

const Talent = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [talent, setTalent] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'table');
  const [showFilters, setShowFilters] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showMassEdit, setShowMassEdit] = useState(false);
  const [massEditForm, setMassEditForm] = useState({});
  const [massEditSaving, setMassEditSaving] = useState(false);
  const [managers, setManagers] = useState([]);

  // Saved filters
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [showSaveFilter, setShowSaveFilter] = useState(false);

  // Compare
  const [showCompare, setShowCompare] = useState(false);
  const [showAddToList, setShowAddToList] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ categories: [], agencies: [] });

  // Search & sort — restore from URL
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'name');
  const [sortDir, setSortDir] = useState(searchParams.get('dir') || 'asc');

  // Filters — restore from URL
  const [filters, setFilters] = useState(() => parseFiltersFromURL(searchParams));
  const skipUrlSync = useRef(false);
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  // Sync state back to URL
  const syncToURL = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (sortBy !== 'name') params.set('sort', sortBy);
    if (sortDir !== 'asc') params.set('dir', sortDir);
    if (viewMode !== 'table') params.set('view', viewMode);
    for (const [key, val] of Object.entries(filtersRef.current)) {
      if (key === 'category_search') continue;
      if (Array.isArray(val) && val.length > 0) params.set(key, val.join(','));
      else if (typeof val === 'string' && val) params.set(key, val);
    }
    setSearchParams(params, { replace: true });
  }, [search, sortBy, sortDir, viewMode, setSearchParams]);

  useEffect(() => {
    if (skipUrlSync.current) { skipUrlSync.current = false; return; }
    const timer = setTimeout(syncToURL, 100);
    return () => clearTimeout(timer);
  }, [search, sortBy, sortDir, viewMode, filters, syncToURL]);

  // Quick add form
  const [newTalent, setNewTalent] = useState({ name: '', primary_category: '', gender: '', agency: '' });

  const fetchTalent = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filters.category.length) params.set('category', filters.category.join(','));
      if (filters.tier.length) params.set('tier', filters.tier.join(','));
      if (filters.gender.length) params.set('gender', filters.gender.join(','));
      if (filters.agency.length) params.set('agency', filters.agency.join(','));

      // Range filters
      Object.entries(filters).forEach(([key, val]) => {
        if (key === 'category_search') return; // client-side only
        if (typeof val === 'string' && val && !['category','tier','gender','agency'].includes(key.replace(/_min|_max/,''))) {
          params.set(key, val);
        }
      });

      params.set('sort_by', sortBy);
      params.set('sort_dir', sortDir);

      const res = await api.get('/talent?' + params.toString());
      setTalent(res.data.talent);
      setTotal(res.data.total);
      if (res.data.filterOptions) setFilterOptions(res.data.filterOptions);
    } catch (e) {
      console.error('Fetch talent error:', e);
    } finally {
      setLoading(false);
    }
  }, [search, filters, sortBy, sortDir]);

  useEffect(() => {
    const timer = setTimeout(() => fetchTalent(), 300);
    return () => clearTimeout(timer);
  }, [fetchTalent]);

  // Fetch saved filters on mount
  useEffect(() => { fetchSavedFilters(); fetchManagers(); }, []);

  const fetchManagers = async () => {
    try { const res = await api.get('/talent-managers'); setManagers(res.data.managers || []); } catch (e) {}
  };

  const fetchSavedFilters = async () => {
    try {
      const res = await api.get('/talent-filters');
      setSavedFilters(res.data.filters || []);
    } catch (e) {}
  };

  const handleBulkEnrich = async () => {
    if (!window.confirm('Pull InsightIQ demographics for all talent with Instagram handles but missing demographics? (up to 50 at a time)')) return;
    setBulkEnriching(true);
    try {
      const res = await api.post('/talent/bulk-enrich');
      const d = res.data;
      const enriched = d.results?.filter(r => r.status === 'enriched').length || 0;
      const errors = d.results?.filter(r => r.status === 'error').length || 0;
      alert(`Bulk enrich complete: ${enriched} enriched, ${errors} errors, ${d.total} processed.`);
      fetchTalent();
    } catch (e) { alert(e.response?.data?.error || 'Bulk enrich failed'); }
    finally { setBulkEnriching(false); }
  };

  const handleSaveFilter = async () => {
    if (!saveFilterName.trim()) return;
    const filterData = { ...filters };
    delete filterData.category_search;
    try {
      await api.post('/talent-filters', { name: saveFilterName.trim(), filter_json: { filters: filterData, search, sortBy, sortDir } });
      setSaveFilterName('');
      setShowSaveFilter(false);
      fetchSavedFilters();
    } catch (e) { alert('Failed to save filter'); }
  };

  const handleLoadFilter = (saved) => {
    const data = typeof saved.filter_json === 'string' ? JSON.parse(saved.filter_json) : saved.filter_json;
    if (data.filters) setFilters({ ...defaultFilters, ...data.filters });
    if (data.search) setSearch(data.search);
    if (data.sortBy) setSortBy(data.sortBy);
    if (data.sortDir) setSortDir(data.sortDir);
  };

  const handleDeleteFilter = async (id) => {
    try { await api.delete(`/talent-filters/${id}`); fetchSavedFilters(); } catch (e) {}
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === talent.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(talent.map(t => t.id)));
  };

  const handleMassEdit = async () => {
    const updates = {};
    Object.entries(massEditForm).forEach(([k, v]) => { if (v !== '') updates[k] = v; });
    if (Object.keys(updates).length === 0) return;
    setMassEditSaving(true);
    try {
      const res = await api.put('/talent/bulk-update', { talent_ids: Array.from(selectedIds), updates });
      setShowMassEdit(false);
      setMassEditForm({});
      setSelectedIds(new Set());
      fetchTalent();
      alert(`Updated ${res.data.updated} creator${res.data.updated !== 1 ? 's' : ''}`);
    } catch (e) {
      alert(e.response?.data?.error || 'Mass edit failed');
    } finally { setMassEditSaving(false); }
  };

  const handleQuickAdd = async () => {
    if (!newTalent.name) return;
    try {
      const res = await api.post('/talent', { ...newTalent, status: 'available' });
      navigate(`/talent/${res.data.talent.id}`);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to add talent');
    }
  };

  const clearFilters = () => {
    setFilters({ ...defaultFilters });
    setSearch('');
  };

  const hasActiveFilters = Object.entries(filters).some(([k, v]) => k !== 'category_search' && (Array.isArray(v) ? v.length > 0 : v !== ''));

  const toggleArrayFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter(v => v !== value) : [...prev[key], value]
    }));
  };

  // RangeFilter is defined outside component to preserve input focus

  // ChipFilter and RangeFilter are defined outside component to preserve input focus

  const TalentCard = ({ t }) => {
    const ti = tierLabel(t.talent_tier);
    return (
      <Link to={`/talent/${t.id}`} className="block bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-600 transition-colors">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-white font-semibold">{t.name}</div>
            <div className="text-zinc-500 text-xs">{formatNum(t.total_following)} total following</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`${bssColor(t.brand_safety_score)} text-xs font-semibold`}>{t.brand_safety_score ? t.brand_safety_score.toFixed(1) : '-'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-2 text-xs">
          <span className={`px-1.5 py-0.5 rounded ${ti.bg} ${ti.color} font-semibold`}>{ti.text}</span>
          {t.twitch_avg_ccv > 0 && <span className="text-purple-400">CCV {formatNum(t.twitch_avg_ccv)}</span>}
          <span className="text-zinc-500">{formatPct(t.aud_18_34)} 18-34</span>
          {t.aud_male && (
            <div className="flex items-center gap-1">
              <div className="w-12 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                <div className="h-full bg-blue-400 rounded-full" style={{ width: `${(t.aud_male * 100)}%` }} />
              </div>
              <span className="text-zinc-500">{formatPct(t.aud_male)} M</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs">
          {t.primary_category && <span className="px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded">{t.primary_category}</span>}
          {t.agency && t.agency !== 'n/a' && <span className="px-2 py-0.5 bg-zinc-800 text-zinc-500 rounded">{t.agency}</span>}
          {t.gender && <span className="text-zinc-600">{t.gender}</span>}
        </div>
      </Link>
    );
  };

  return (
    <div className="flex h-full min-w-0 overflow-hidden">
      {/* Filter Sidebar */}
      {showFilters && (
        <div className="w-56 flex-shrink-0 border-r border-zinc-800 overflow-y-auto p-3" style={{ maxHeight: 'calc(100vh - 60px)' }}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-white text-sm font-semibold">Filters</span>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-xs text-nextide-turquoise hover:underline">Clear all</button>
            )}
          </div>

          {/* Saved Filters */}
          <div className="mb-3 pb-3 border-b border-zinc-800">
            <div className="flex items-center justify-between mb-1">
              <span className="text-zinc-500 text-xs">Saved Filters</span>
              {hasActiveFilters && !showSaveFilter && (
                <button onClick={() => setShowSaveFilter(true)} className="text-xs text-nextide-turquoise hover:underline">Save current</button>
              )}
            </div>
            {showSaveFilter && (
              <div className="mb-2">
                <div className="flex gap-1">
                  <input type="text" placeholder="Filter name..." value={saveFilterName}
                    onChange={e => setSaveFilterName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveFilter()}
                    className="flex-1 min-w-0 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-xs" autoFocus />
                  <button onClick={() => { setShowSaveFilter(false); setSaveFilterName(''); }}
                    className="text-zinc-500 hover:text-white px-1 text-xs shrink-0">x</button>
                </div>
                <button onClick={handleSaveFilter} disabled={!saveFilterName.trim()}
                  className="w-full mt-1 bg-nextide-turquoise text-black px-2 py-1 rounded text-xs font-semibold disabled:opacity-50">Save Filter</button>
              </div>
            )}
            {savedFilters.length > 0 ? (
              <div className="space-y-1">
                {savedFilters.map(sf => (
                  <div key={sf.id} className="flex items-center gap-1 group">
                    <button onClick={() => handleLoadFilter(sf)}
                      className="flex-1 text-left px-2 py-1 rounded text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white truncate">
                      {sf.name}
                    </button>
                    <button onClick={() => handleDeleteFilter(sf.id)}
                      className="text-zinc-700 group-hover:text-zinc-500 hover:text-red-400 text-xs px-1 opacity-0 group-hover:opacity-100 transition-opacity">x</button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-zinc-700 text-xs">No saved filters</div>
            )}
          </div>

          <ChipFilter label="Tier" options={['1','2','3']} filterKey="tier" filters={filters} toggleArrayFilter={toggleArrayFilter} />
          <div className="mb-3">
            <div className="text-zinc-500 text-xs mb-1">Category</div>
            <input type="text" placeholder="Search categories..." value={filters.category_search || ''}
              onChange={e => setFilters(prev => ({...prev, category_search: e.target.value}))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-xs mb-1" />
            <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
              {filterOptions.categories
                .filter(c => !filters.category_search || c.toLowerCase().includes(filters.category_search.toLowerCase()))
                .slice(0, 20)
                .map(o => (
                <button key={o} onClick={() => toggleArrayFilter('category', o)}
                  className={`px-2 py-0.5 rounded text-xs truncate max-w-full ${filters.category.includes(o) ? 'bg-nextide-turquoise text-black font-semibold' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
                  {o.length > 25 ? o.substring(0, 25) + '...' : o}
                </button>
              ))}
            </div>
          </div>
          <ChipFilter label="Gender" options={['Male','Female','Non-binary']} filterKey="gender" filters={filters} toggleArrayFilter={toggleArrayFilter} />
          {filterOptions.agencies.length > 0 && <ChipFilter label="Agency" options={filterOptions.agencies} filterKey="agency" filters={filters} toggleArrayFilter={toggleArrayFilter} />}

          <div className="border-t border-zinc-800 mt-3 pt-3">
            <div className="text-zinc-400 text-xs font-semibold mb-2">PLATFORM</div>
            <RangeFilter label="Twitch CCV (L30)" minKey="twitch_ccv_min" maxKey="twitch_ccv_max" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Twitch Followers" minKey="twitch_followers_min" maxKey="twitch_followers_max" filters={filters} setFilters={setFilters} />
            <RangeFilter label="YouTube Followers" minKey="youtube_followers_min" maxKey="youtube_followers_max" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Instagram Followers" minKey="instagram_followers_min" maxKey="instagram_followers_max" filters={filters} setFilters={setFilters} />
            <RangeFilter label="TikTok Followers" minKey="tiktok_followers_min" maxKey="tiktok_followers_max" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Twitter Followers" minKey="twitter_followers_min" maxKey="twitter_followers_max" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Total Following" minKey="total_following_min" maxKey="total_following_max" filters={filters} setFilters={setFilters} />
          </div>

          <div className="border-t border-zinc-800 mt-3 pt-3">
            <div className="text-zinc-400 text-xs font-semibold mb-2">DEMOGRAPHICS</div>
            <RangeFilter label="Aud. Male %" minKey="aud_male_min" maxKey="aud_male_max" suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. Female %" minKey="aud_female_min" maxKey="aud_female_max" suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. U18 %"    minKey="aud_u18_min"      maxKey="aud_u18_max"      suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. 18-24 %"  minKey="aud_18_24_min"   maxKey="aud_18_24_max"   suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. 25-34 %"  minKey="aud_25_34_min"   maxKey="aud_25_34_max"   suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. 35-44 %"  minKey="aud_35_44_min"   maxKey="aud_35_44_max"   suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. 45-54 %"  minKey="aud_45_54_min"   maxKey="aud_45_54_max"   suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. 55+ %"    minKey="aud_55_plus_min" maxKey="aud_55_plus_max" suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. Black/AA %" minKey="aud_black_min" maxKey="aud_black_max" suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. White %" minKey="aud_white_min" maxKey="aud_white_max" suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. Asian %" minKey="aud_asian_min" maxKey="aud_asian_max" suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. Hispanic %" minKey="aud_hispanic_min" maxKey="aud_hispanic_max" suffix="%" filters={filters} setFilters={setFilters} />
            <div className="text-zinc-400 text-xs font-semibold mt-2 mb-1">GEOGRAPHY</div>
            <RangeFilter label="Aud. USA %" minKey="aud_usa_min" maxKey="aud_usa_max" suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. Canada %" minKey="aud_canada_min" maxKey="aud_canada_max" suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. UK %" minKey="aud_uk_min" maxKey="aud_uk_max" suffix="%" filters={filters} setFilters={setFilters} />
            <RangeFilter label="Aud. Australia %" minKey="aud_australia_min" maxKey="aud_australia_max" suffix="%" filters={filters} setFilters={setFilters} />
          </div>

          <div className="border-t border-zinc-800 mt-3 pt-3">
            <div className="text-zinc-400 text-xs font-semibold mb-2">BRAND SAFETY</div>
            <div className="mb-3">
              <div className="text-zinc-500 text-xs mb-1">Min BSS Score</div>
              <input type="number" step="0.1" min="0" max="5" placeholder="0.0" value={filters.bss_min}
                onChange={e => setFilters(prev => ({...prev, bss_min: e.target.value}))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-xs" />
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-4 mb-1">
              <Link to="/talent-metrics" className="text-zinc-400 hover:text-white text-sm">Metrics</Link>
              <span className="text-white text-sm font-semibold border-b-2 border-nextide-turquoise pb-0.5">Network</span>
              <Link to="/talent-lists" className="text-zinc-400 hover:text-white text-sm">Lists</Link>
            </div>
            <h1 className="text-2xl font-bold text-white">Talent Network</h1>
            <p className="text-zinc-400 text-sm mt-1">{total} creator{total !== 1 ? 's' : ''}{hasActiveFilters ? ' (filtered)' : ''}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-2 rounded-lg text-sm ${showFilters ? 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
              Filters
            </button>
            <button onClick={() => setShowImport(true)} className="bg-zinc-800 border border-zinc-700 text-white px-4 py-2 rounded-lg text-sm hover:border-zinc-600">
              Import
            </button>
            <button onClick={handleBulkEnrich} disabled={bulkEnriching}
              className="bg-purple-600/20 border border-purple-700/30 text-purple-400 px-4 py-2 rounded-lg text-sm hover:bg-purple-600/30 disabled:opacity-50">
              {bulkEnriching ? 'Enriching...' : '✦ Enrich All'}
            </button>
            <button onClick={() => setShowAddForm(true)} className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold">
              + Add Talent
            </button>
          </div>
        </div>

        {/* Search + Quick Sort + View Toggle */}
        <div className="flex items-center gap-3 mb-4">
          <input type="text" placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)}
            className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm" />
          <div className="flex bg-zinc-800 rounded-lg p-0.5 gap-0.5">
            {[
              { key: 'name',            label: 'Name'  },
              { key: 'total_following', label: 'Total' },
              { key: 'twitch_ccv',      label: 'CCV'   },
              { key: 'aud_usa',         label: 'USA%'  },
              { key: 'aud_canada',      label: 'CA%'   },
              { key: 'aud_uk',          label: 'UK%'   },
              { key: 'aud_australia',   label: 'AUS%'  },
            ].map(s => (
              <button key={s.key} onClick={() => {
                if (sortBy === s.key) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
                else { setSortBy(s.key); setSortDir(s.key === 'name' ? 'asc' : 'desc'); }
              }}
                className={`px-2.5 py-1.5 rounded text-xs font-medium whitespace-nowrap ${sortBy === s.key ? 'bg-nextide-turquoise text-black' : 'text-zinc-400 hover:text-white'}`}>
                {s.label}{sortBy === s.key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
              </button>
            ))}
          </div>
          <div className="flex bg-zinc-800 rounded-lg p-0.5">
            <button onClick={() => setViewMode('table')} className={`px-3 py-1.5 rounded text-xs font-medium ${viewMode === 'table' ? 'bg-nextide-turquoise text-black' : 'text-zinc-400'}`}>Table</button>
            <button onClick={() => setViewMode('grid')} className={`px-3 py-1.5 rounded text-xs font-medium ${viewMode === 'grid' ? 'bg-nextide-turquoise text-black' : 'text-zinc-400'}`}>Grid</button>
          </div>
        </div>

        {/* Selection Action Bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 mb-3 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2">
            <span className="text-white text-sm font-medium">{selectedIds.size} selected</span>
            <button onClick={() => { setMassEditForm({}); setShowMassEdit(true); }}
              className="bg-nextide-turquoise text-black px-3 py-1 rounded text-sm font-semibold">Mass Edit</button>
            {selectedIds.size >= 2 && selectedIds.size <= 4 && (
              <button onClick={() => setShowCompare(true)}
                className="bg-zinc-700 text-white px-3 py-1 rounded text-sm font-semibold hover:bg-zinc-600">Compare</button>
            )}
            <button onClick={() => setShowAddToList(true)}
              className="bg-zinc-700 text-white px-3 py-1 rounded text-sm font-semibold hover:bg-zinc-600">Add to List</button>
            <button onClick={() => setSelectedIds(new Set())}
              className="text-zinc-400 hover:text-white text-sm ml-auto">Clear selection</button>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-zinc-500 text-center py-16">Loading talent...</div>
        ) : talent.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-zinc-500 text-lg mb-2">{hasActiveFilters ? 'No talent matches your filters' : 'No talent in your network yet'}</div>
            <div className="text-zinc-600 text-sm">{hasActiveFilters ? 'Try adjusting your filters' : 'Import your roster or add talent manually'}</div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-3 gap-4">
            {talent.map(t => <TalentCard key={t.id} t={t} />)}
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="w-10 p-3"><input type="checkbox" checked={selectedIds.size === talent.length && talent.length > 0} onChange={selectAll} className="cursor-pointer" /></th>
                  <th className="text-left p-3 text-zinc-400 text-xs font-medium">Name</th>
                  <th className="text-left p-3 text-zinc-400 text-xs font-medium">Tier</th>
                  <th className="text-left p-3 text-zinc-400 text-xs font-medium">BSS</th>
                  <th className="text-left p-3 text-zinc-400 text-xs font-medium">Category</th>
                  <th className="text-right p-3 text-zinc-400 text-xs font-medium">Total</th>
                  <th className="text-right p-3 text-zinc-400 text-xs font-medium">CCV</th>
                  <th className="text-right p-3 text-zinc-400 text-xs font-medium">USA%</th>
                  <th className="text-right p-3 text-zinc-400 text-xs font-medium">CA%</th>
                  <th className="text-right p-3 text-zinc-400 text-xs font-medium">UK%</th>
                  <th className="text-right p-3 text-zinc-400 text-xs font-medium">AUS%</th>
                  <th className="text-left p-3 text-zinc-400 text-xs font-medium">Agency</th>
                </tr>
              </thead>
              <tbody>
                {talent.map(t => {
                  const ti = tierLabel(t.talent_tier);
                  return (
                    <tr key={t.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer" onClick={() => navigate(`/talent/${t.id}`)}>
                      <td className="p-3" onClick={e => e.stopPropagation()}><input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleSelect(t.id)} className="cursor-pointer" /></td>
                      <td className="p-3"><span className="text-white font-medium text-sm">{t.name}</span></td>
                      <td className="p-3"><span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${ti.bg} ${ti.color}`}>{ti.text}</span></td>
                      <td className="p-3"><span className={`text-xs font-semibold ${bssColor(t.brand_safety_score)}`}>{t.brand_safety_score ? parseFloat(t.brand_safety_score).toFixed(1) : '-'}</span></td>
                      <td className="p-3 text-zinc-400 text-xs">{t.primary_category || '-'}</td>
                      <td className="p-3 text-right text-nextide-turquoise text-xs font-medium">{formatNum(t.total_following)}</td>
                      <td className="p-3 text-right text-purple-400 text-xs">{t.twitch_avg_ccv > 0 ? formatNum(t.twitch_avg_ccv) : '-'}</td>
                      <td className="p-3 text-right text-zinc-300 text-xs">{formatPct(t.aud_usa)}</td>
                      <td className="p-3 text-right text-zinc-300 text-xs">{formatPct(t.aud_canada)}</td>
                      <td className="p-3 text-right text-zinc-300 text-xs">{formatPct(t.aud_uk)}</td>
                      <td className="p-3 text-right text-zinc-300 text-xs">{formatPct(t.aud_australia)}</td>
                      <td className="p-3 text-zinc-500 text-xs">{t.agency && t.agency !== 'n/a' ? t.agency : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Add Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowAddForm(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-white mb-4">Quick Add Talent</h2>
            <div className="space-y-3">
              <div><label className="block text-xs text-zinc-400 mb-1">Creator Name *</label>
                <input type="text" value={newTalent.name} onChange={e => setNewTalent({...newTalent, name: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="JeromeASF" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs text-zinc-400 mb-1">Category</label>
                  <input type="text" value={newTalent.primary_category} onChange={e => setNewTalent({...newTalent, primary_category: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Minecraft" /></div>
                <div><label className="block text-xs text-zinc-400 mb-1">Gender</label>
                  <select value={newTalent.gender} onChange={e => setNewTalent({...newTalent, gender: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="">Select...</option><option>Male</option><option>Female</option><option>Non-binary</option>
                  </select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs text-zinc-400 mb-1">Agency</label>
                  <input type="text" value={newTalent.agency} onChange={e => setNewTalent({...newTalent, agency: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="UTA" /></div>

              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowAddForm(false)} className="flex-1 bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={handleQuickAdd} disabled={!newTalent.name}
                className="flex-1 bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                Add & Edit Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      <TalentImportModal isOpen={showImport} onClose={() => setShowImport(false)} onComplete={() => { setShowImport(false); fetchTalent(); }} />

      {/* Mass Edit Modal */}
      {showMassEdit && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowMassEdit(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-white mb-1">Mass Edit</h2>
            <p className="text-zinc-400 text-sm mb-4">Update {selectedIds.size} selected creator{selectedIds.size !== 1 ? 's' : ''}. Only filled fields will be changed.</p>
            <div className="space-y-3">
              <div><label className="block text-xs text-zinc-400 mb-1">Manager</label>
                <select value={massEditForm.manager_name || ''} onChange={e => {
                  const name = e.target.value;
                  if (name === '__new__') {
                    const newName = prompt('New manager name:');
                    const newEmail = prompt('Manager email:');
                    const newAgency = prompt('Agency:');
                    if (newName) setMassEditForm(prev => ({...prev, manager_name: newName, manager_email: newEmail || '', agency: newAgency || prev.agency || ''}));
                  } else {
                    const mgr = managers.find(m => m.name === name);
                    setMassEditForm(prev => ({...prev, manager_name: name, manager_email: name === 'Self' ? '' : (mgr?.email || prev.manager_email || ''), agency: mgr?.agency || prev.agency || ''}));
                  }
                }} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                  <option value="">Don't change</option>
                  <option value="Self">Self (not repped)</option>
                  {managers.map(m => <option key={m.name} value={m.name}>{m.name}{m.agency ? ` (${m.agency})` : ''}{m.email ? ` — ${m.email}` : ''}</option>)}
                  <option value="__new__">+ Add new manager...</option>
                </select></div>
              {massEditForm.manager_name && massEditForm.manager_name !== 'Self' && (
                <div><label className="block text-xs text-zinc-400 mb-1">Manager Email</label>
                  <input type="text" value={massEditForm.manager_email || ''} onChange={e => setMassEditForm(prev => ({...prev, manager_email: e.target.value}))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" /></div>
              )}
              <div><label className="block text-xs text-zinc-400 mb-1">Agency</label>
                <input type="text" value={massEditForm.agency || ''} onChange={e => setMassEditForm(prev => ({...prev, agency: e.target.value}))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs text-zinc-400 mb-1">Talent Tier</label>
                  <select value={massEditForm.talent_tier || ''} onChange={e => setMassEditForm(prev => ({...prev, talent_tier: e.target.value}))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="">Don't change</option><option value="1">Tier 1</option><option value="2">Tier 2</option><option value="3">Tier 3</option>
                  </select></div>
<div></div>
              </div>
              <div><label className="block text-xs text-zinc-400 mb-1">Primary Category</label>
                <input type="text" value={massEditForm.primary_category || ''} onChange={e => setMassEditForm(prev => ({...prev, primary_category: e.target.value}))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Leave blank to skip" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs text-zinc-400 mb-1">OmniBanner CPM ($)</label>
                  <input type="number" step="0.01" value={massEditForm.rate_omnibanner_cpm || ''} onChange={e => setMassEditForm(prev => ({...prev, rate_omnibanner_cpm: e.target.value}))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Leave blank to skip" /></div>
                <div><label className="block text-xs text-zinc-400 mb-1">Immersive Frame CPM ($)</label>
                  <input type="number" step="0.01" value={massEditForm.rate_immersive_frame_cpm || ''} onChange={e => setMassEditForm(prev => ({...prev, rate_immersive_frame_cpm: e.target.value}))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Leave blank to skip" /></div>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowMassEdit(false)} className="flex-1 bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={handleMassEdit} disabled={massEditSaving}
                className="flex-1 bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                {massEditSaving ? 'Updating...' : `Update ${selectedIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compare Modal */}
      {showCompare && (
        <TalentCompare
          talents={talent.filter(t => selectedIds.has(t.id))}
          onClose={() => setShowCompare(false)}
        />
      )}

      {/* Add to List Modal */}
      <AddToListModal
        isOpen={showAddToList}
        onClose={() => setShowAddToList(false)}
        talentIds={Array.from(selectedIds)}
        onAdded={(listId, count) => {
          setSelectedIds(new Set());
          alert(`Added ${count} creator${count !== 1 ? 's' : ''} to list`);
        }}
      />
    </div>
  );
};

export default Talent;
